import React, { useRef, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Share2, Image } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import {
  renderQuoteCard,
  canvasToBlob,
  QUOTE_CARD_THEMES,
  QuoteCardData,
  QuoteCardTheme,
} from '@/utils/quoteCardRenderer';

interface QuoteCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: QuoteCardData;
}

type CardFormat = 'square' | 'story';

const QuoteCardDialog: React.FC<QuoteCardDialogProps> = ({ open, onOpenChange, data }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selectedTheme, setSelectedTheme] = useState<QuoteCardTheme>(QUOTE_CARD_THEMES[0]);
  const [format, setFormat] = useState<CardFormat>('square');

  useEffect(() => {
    if (open && canvasRef.current) {
      renderQuoteCard(canvasRef.current, data, selectedTheme, format);
    }
  }, [open, data, selectedTheme, format]);

  const handleDownload = async () => {
    if (!canvasRef.current) return;
    try {
      const blob = await canvasToBlob(canvasRef.current);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kidquotes-${data.childName.toLowerCase()}-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Downloaded!', description: 'Quote card saved to your device.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to download image.', variant: 'destructive' });
    }
  };

  const handleShare = async () => {
    if (!canvasRef.current) return;
    try {
      const blob = await canvasToBlob(canvasRef.current);
      const file = new File([blob], 'kidquotes.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Quote from ${data.childName}`,
        });
      } else {
        // Fallback to download
        handleDownload();
      }
    } catch (error) {
      // User cancelled share is not an error
      if (error instanceof Error && error.name !== 'AbortError') {
        toast({ title: 'Error', description: 'Failed to share image.', variant: 'destructive' });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg w-[95%] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="h-4 w-4" />
            Create Quote Card
          </DialogTitle>
        </DialogHeader>

        {/* Canvas Preview */}
        <div className="flex justify-center">
          <canvas
            ref={canvasRef}
            className="rounded-lg shadow-md w-full"
            style={{
              maxHeight: format === 'story' ? '400px' : '300px',
              objectFit: 'contain',
            }}
          />
        </div>

        {/* Format Toggle */}
        <div className="flex gap-2 justify-center">
          <Button
            variant={format === 'square' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFormat('square')}
          >
            Square
          </Button>
          <Button
            variant={format === 'story' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFormat('story')}
          >
            Story
          </Button>
        </div>

        {/* Theme Picker */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Theme</p>
          <div className="flex gap-2 flex-wrap">
            {QUOTE_CARD_THEMES.map((theme) => (
              <button
                key={theme.id}
                onClick={() => setSelectedTheme(theme)}
                className={`w-10 h-10 rounded-full border-2 transition-all ${
                  selectedTheme.id === theme.id
                    ? 'border-primary scale-110 ring-2 ring-primary/30'
                    : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: theme.background }}
                title={theme.name}
              />
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={handleDownload} variant="outline" className="flex-1">
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button onClick={handleShare} className="flex-1">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteCardDialog;
